import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios, { AxiosError } from 'axios';
import { ArrowLeft, AlertCircle, Home } from '../components/Icons';
import { API } from '../contexts/AuthContext';
import { type Currency, formatPrice } from '../lib/currency';

type Cadence  = 'monthly' | 'annual';
type SkuKind  = 'topup' | 'subscription' | 'free';

interface DisplayPrice { INR: string; USD: string; }
interface Sku {
  id: string; kind: SkuKind; credits: string;
  cadence: Cadence | null; displayPrice: DisplayPrice;
}
interface PricingResponse {
  currency: Currency | null; lockedCurrency: Currency | null; skus: Sku[];
}
interface CheckoutResponse { checkoutUrl: string; }
interface ApiErrorBody { error?: string; message?: string; }
interface CreditsData {
  balance: string; subscriptionBalance: string; permanentBalance: string;
  planName: string | null;
}

function planNameFromSkuId(id: string): string {
  const tier = (id.split(/[_-]/)[0] ?? id).toLowerCase();
  return tier.charAt(0).toUpperCase() + tier.slice(1);
}

function getPlanMeta(sku: Sku, subIndex: number): string {
  if (sku.kind === 'free') return `${sku.credits} credits · one-time`;
  const period = sku.cadence === 'annual' ? '/yr' : '/mo';
  const base   = `${sku.credits} credits${period}`;
  if (subIndex === 0) return `${base} · priority queue`;
  if (subIndex === 1) return `${base} · team seats · no watermark`;
  return base;
}

function isPositiveBalance(value: string | undefined | null): boolean {
  if (!value) return false;
  const n = Number(value);
  return Number.isFinite(n) && n > 0;
}

function Credits() {
  const navigate = useNavigate();

  const [creditsData, setCreditsData]       = useState<CreditsData | null>(null);
  const [pricingData, setPricingData]       = useState<PricingResponse | null>(null);
  const [loading, setLoading]               = useState<boolean>(true);
  const [error, setError]                   = useState<string>('');
  const [successMessage, setSuccessMessage] = useState<string>('');
  const [portalLoading, setPortalLoading]   = useState<boolean>(false);
  const [selectedCurrency, setSelectedCurrency] = useState<Currency>('INR');
  const [cadence, setCadence]               = useState<Cadence>('monthly');
  const [loadingSkuId, setLoadingSkuId]     = useState<string | null>(null);
  const [purchaseError, setPurchaseError]   = useState<string>('');
  const [selectedSkuId, setSelectedSkuId]   = useState<string | null>(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('status') === 'success') {
      setSuccessMessage('Payment successful! Your credits have been added.');
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const [cRes, pRes] = await Promise.all([
          axios.get<CreditsData>(`${API}/credits`),
          axios.get<PricingResponse>(`${API}/credits/pricing`),
        ]);
        setCreditsData(cRes.data);
        setPricingData(pRes.data);
        const defaultCurrency = pRes.data.lockedCurrency ?? pRes.data.currency;
        if (defaultCurrency !== null) setSelectedCurrency(defaultCurrency);
      } catch {
        setError('Failed to load credits. Please try again.');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  async function handleManageSubscription(): Promise<void> {
    setPortalLoading(true);
    setError('');
    try {
      const res = await axios.post<{ portalUrl: string }>(`${API}/payments/portal`);
      window.location.href = res.data.portalUrl;
    } catch {
      setError('Failed to open subscription portal. Please try again.');
      setPortalLoading(false);
    }
  }

  async function handlePurchase(skuId: string, kind: SkuKind): Promise<void> {
    if (loadingSkuId) return;
    setLoadingSkuId(skuId);
    setPurchaseError('');
    try {
      const endpoint = kind === 'topup' ? '/payments/checkout' : '/payments/subscribe';
      const res = await axios.post<CheckoutResponse>(`${API}${endpoint}`, {
        sku: skuId, currency: selectedCurrency,
      });
      window.location.href = res.data.checkoutUrl;
    } catch (err) {
      const axiosErr = err as AxiosError<ApiErrorBody>;
      const msg = axiosErr.response?.data?.error
               ?? axiosErr.response?.data?.message
               ?? 'Failed to start checkout. Please try again.';
      setPurchaseError(msg);
      setLoadingSkuId(null);
    }
  }

  const { topupSkus, subscriptionSkus, hasAnySubscription, freeSkus } = useMemo(() => {
    const skus = pricingData?.skus ?? [];
    return {
      freeSkus:           skus.filter(s => s.kind === 'free'),
      topupSkus:          skus.filter(s => s.kind === 'topup'),
      subscriptionSkus:   skus.filter(s => s.kind === 'subscription' && s.cadence === cadence),
      hasAnySubscription: skus.some(s => s.kind === 'subscription'),
    };
  }, [pricingData, cadence]);

  const allSelectableSkus = useMemo(
    () => [...subscriptionSkus, ...topupSkus],
    [subscriptionSkus, topupSkus],
  );

  const selectedSku = useMemo(
    () => allSelectableSkus.find(s => s.id === selectedSkuId) ?? null,
    [allSelectableSkus, selectedSkuId],
  );

    function ctaText(): { label: string; price: string } {
    if (!selectedSku) return { label: '', price: '' };
    const price = formatPrice(selectedSku.displayPrice[selectedCurrency], selectedCurrency);
    if (selectedSku.kind === 'subscription') {
      const period   = selectedSku.cadence === 'annual' ? '/yr' : '/mo';
      const planName = planNameFromSkuId(selectedSku.id);
      return { label: `Subscribe to ${planName} (${selectedSku.credits} credits)`, price: `${price}${period}` };
    }
    return { label: `Buy ${selectedSku.credits} Credits`, price };
  }

  function toggleSku(id: string) {
    setSelectedSkuId(prev => (prev === id ? null : id));
  }

  const showBreakdown    = isPositiveBalance(creditsData?.subscriptionBalance) && isPositiveBalance(creditsData?.permanentBalance);
  const showManageButton = isPositiveBalance(creditsData?.subscriptionBalance);
  const isOnFreePlan     = creditsData !== null && creditsData.planName === null;
  const dataReady        = !loading && !error && creditsData !== null && pricingData !== null;

  return (
    <div className="page-root">
      <header className="app-header">
        <div className="header-inner">
          <button className="header-action-btn" onClick={() => navigate('/profile')} aria-label="Back to profile">
            <ArrowLeft size={20} />
          </button>
          <div style={{ flex: 1 }} />
          <button className="header-action-btn" onClick={() => navigate('/home')} aria-label="Home">
            <Home size={20} />
          </button>
        </div>
      </header>

      <div className="page-scroll">
        <div className="plans-page-content">

          {successMessage && (
            <div className="credits-success-toast" role="status">{successMessage}</div>
          )}

          {loading && (
            <div style={{ display: 'flex', justifyContent: 'center', paddingTop: 60 }}>
              <div className="spinner" />
            </div>
          )}

          {(error || purchaseError) && (
            <div className="alert alert-error" role="alert" style={{ marginBottom: 16 }}>
              <AlertCircle size={16} style={{ flexShrink: 0 }} />
              <span>{error || purchaseError}</span>
            </div>
          )}

          {dataReady && creditsData && pricingData && (
            <>
              {/* Balance card */}
              <div className="credits-balance-card">
                <div className="credits-balance-amount">{creditsData.balance}</div>
                <div className="credits-balance-label">credits remaining</div>
                {showBreakdown && (
                  <div className="credits-balance-breakdown">
                    <span className="breakdown-badge">{creditsData.subscriptionBalance} subscription</span>
                    <span className="breakdown-badge">{creditsData.permanentBalance} permanent</span>
                  </div>
                )}
              </div>

              {/* Header row: title left, currency toggle right */}
              <div className="plans-header-row">
                <div>
                  <h2 className="plans-page-title">Pick a plan</h2>
                  <p className="plans-page-subtitle">Cancel anytime · credits roll over for 30 days</p>
                </div>
                <div className="plans-toggle-group" style={{ flexShrink: 0 }}>
                  {(['INR', 'USD'] as Currency[]).map(c => (
                    <button
                      key={c}
                      type="button"
                      className={`plans-toggle-btn${selectedCurrency === c ? ' active' : ''}`}
                      onClick={() => setSelectedCurrency(c)}
                    >{c}</button>
                  ))}
                </div>
              </div>

              {/* Current plan card */}
              <div className="plan-current-card">
                <span className={`plan-current-dot${isOnFreePlan ? '' : ' plan-current-dot--paid'}`} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div className="plan-current-name">
                    {isOnFreePlan
                      ? "You're on the Free plan"
                      : `You're on the ${creditsData.planName} plan`}
                  </div>
                  {isOnFreePlan && freeSkus[0] && (
                    <div className="plan-current-meta">{getPlanMeta(freeSkus[0], -1)}</div>
                  )}
                </div>
                {showManageButton && (
                  <button
                    type="button"
                    className="plan-manage-btn"
                    onClick={handleManageSubscription}
                    disabled={portalLoading}
                  >
                    {portalLoading ? '…' : 'Manage'}
                  </button>
                )}
              </div>

              {/* Subscription section */}
              {hasAnySubscription && (
                <>
                  <div className="plans-section-label-row">
                    <span className="plans-section-label">Subscription</span>
                    <div className="plans-toggle-group">
                      <button
                        type="button"
                        className={`plans-toggle-btn${cadence === 'monthly' ? ' active' : ''}`}
                        onClick={() => setCadence('monthly')}
                      >Monthly</button>
                      <button
                        type="button"
                        className={`plans-toggle-btn${cadence === 'annual' ? ' active' : ''}`}
                        onClick={() => setCadence('annual')}
                      >Annual <span className="plans-toggle-save">−20%</span></button>
                    </div>
                  </div>

                  <div className="plans-list" style={{ marginBottom: 20 }}>
                    {subscriptionSkus.length > 0 ? subscriptionSkus.map((sku, idx) => {
                      const isPopular   = idx === 0;
                      const periodLabel = sku.cadence === 'annual' ? '/yr' : '/mo';
                      const isSelected  = selectedSkuId === sku.id;
                      const priceStr    = formatPrice(sku.displayPrice[selectedCurrency], selectedCurrency);
                      return (
                        <div
                          key={sku.id}
                          className={`plan-row-card plan-row-card--clickable${isPopular ? ' plan-row-card--popular' : ''}${isSelected ? ' plan-row-card--selected' : ''}`}
                          onClick={() => toggleSku(sku.id)}
                          role="button"
                          tabIndex={0}
                          onKeyDown={e => e.key === 'Enter' && toggleSku(sku.id)}
                        >
                          <div className="plan-row-info">
                            <div className="plan-row-name-row">
                              <span className="plan-row-name">{planNameFromSkuId(sku.id)}</span>
                              {isPopular && <span className="plan-popular-badge-inline">POPULAR</span>}
                            </div>
                            <div className="plan-row-meta">{getPlanMeta(sku, idx)}</div>
                          </div>
                          <div className="plan-row-price-col">
                            <span className="plan-row-price">{priceStr}</span>
                            <span className="plan-row-period">{periodLabel}</span>
                          </div>
                        </div>
                      );
                    }) : (
                      <div className="coming-soon-banner">Annual plans coming soon</div>
                    )}
                  </div>
                </>
              )}

              {/* Top-up packs */}
              {topupSkus.length > 0 && (
                <div className="plans-packs-section">
                  <div className="plans-section-divider">
                    <span>Top Up Credits · One-Time</span>
                  </div>
                  <div className="plans-packs-list">
                    {topupSkus.map(sku => {
                      const isSelected = selectedSkuId === sku.id;
                      const isBusy     = loadingSkuId === sku.id;
                      return (
                        <div
                          key={sku.id}
                          className={`plan-pack-row${isSelected ? ' plan-pack-row--selected' : ''}`}
                          onClick={() => toggleSku(sku.id)}
                          role="button"
                          tabIndex={0}
                          onKeyDown={e => e.key === 'Enter' && toggleSku(sku.id)}
                        >
                          <div className={`plan-pack-radio${isSelected ? ' plan-pack-radio--on' : ''}`}>
                            {isSelected && <div className="plan-pack-radio-dot" />}
                          </div>
                          <div className="plan-row-info">
                            <div className="plan-pack-credits">
                              <span className="plan-pack-bullet">•</span> {sku.credits} credits
                            </div>
                            <div className="plan-row-meta">never expire</div>
                          </div>
                          <div className="plan-row-price-col">
                            {isBusy
                              ? <span className="spinner" style={{ width: 18, height: 18, borderWidth: 2 }} />
                              : <span className="plan-row-price">
                                  {formatPrice(sku.displayPrice[selectedCurrency], selectedCurrency)}
                                </span>
                            }
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Spacer for sticky CTA */}
              <div style={{ height: selectedSku ? 104 : 56 }} />
            </>
          )}

        </div>
      </div>

      {/* Sticky CTA bar */}
      {dataReady && (
        <div className="plans-sticky-cta">
          <div className="plans-sticky-cta-inner">
            {selectedSku && (() => {
              const { label, price } = ctaText();
              return (
                <button
                  type="button"
                  className="plans-cta-btn"
                  disabled={loadingSkuId !== null}
                  onClick={() => selectedSku && handlePurchase(selectedSku.id, selectedSku.kind)}
                >
                  {loadingSkuId === selectedSku.id ? (
                    <span>Loading…</span>
                  ) : (
                    <>
                      <span className="plans-cta-label">{label}</span>
                      <span className="plans-cta-price">{price} →</span>
                    </>
                  )}
                </button>
              );
            })()}
            <p className="plans-cta-secured">Secured by Dodo Payments</p>
          </div>
        </div>
      )}
    </div>
  );
}

export default Credits;
